import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { TenantViewStore } from "../../store/TenantViewStore";
import { useRootStore } from "../../../../store/contexts";
import { EsignatureProvider } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";

export type TenantViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  eSignatureProviderOptions: { id: string; displayId: string }[];
  eSignatureProvider: EsignatureProvider | "";
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  tenantTypeValue: { displayId: string; id: string } | null;
  statusValue: { displayId: string; id: string } | null;
  planValue: { displayId: string; id: string } | null;
  logoFiles: CrudRows;
  logoViewAction?: IMenuAction[];
  publicUrlValue?: { displayId: string; id: string } | null;
  publicUrl: string;
  idERPValue?: { displayId: string; id: string } | null;
  idERP: string;
  idCRMValue?: { displayId: string; id: string } | null;
  idCRM: string;
  monthlyTokenLimitValue?: { displayId: string; id: string } | null;
  monthlyTokenLimit: number | undefined;
  currentMonthTokensValue?: { displayId: string; id: string } | null;
  currentMonthTokens: number;
  tokenLimitResetDayValue?: { displayId: string; id: string } | null;
  tokenLimitResetDay: number;
  lastTokenResetDateValue?: { displayId: string; id: string } | null;
  lastTokenResetDate: Date | null;
  ragLlmProviderValue: { displayId: string; id: string } | null;
  ragLlmModelValue?: { displayId: string; id: string } | null;
  ragLlmModel: string;
  currentStorageBytesValue?: { displayId: string; id: string } | null;
  currentStorageBytes: number;
  storageByteLimitValue?: { displayId: string; id: string } | null;
  storageByteLimit: number | undefined;
  tenantViewStore: TenantViewStore;
};

export const TenantViewForm: React.FC<TenantViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    eSignatureProviderOptions = [],
    eSignatureProvider = "" as EsignatureProvider | "",
    name = "",
    tenantTypeValue,
    statusValue,
    planValue,
    logoFiles,
    logoViewAction,
    publicUrl = "",
    idERP = "",
    idCRM = "",
    monthlyTokenLimit = 0,
    currentMonthTokens = 0,
    tokenLimitResetDay = 0,
    lastTokenResetDate = null,
    ragLlmProviderValue,
    ragLlmModel = "",
    currentStorageBytes = 0,
    storageByteLimit = 0,
    tenantViewStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="null"
            title={langStore.groupTenantLabel}
            collapsible={true}
          >
            <FormControl fullWidth>
              <ShowString
                value={
                  eSignatureProvider
                    ? eSignatureProviderOptions.find(
                        (o: { id: string }) => o.id === eSignatureProvider
                      )?.displayId || eSignatureProvider
                    : ""
                }
                label={langStore.TenantESignatureProviderLabel}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="tenant"
            title={langStore.groupTenantTenantLabel}
            collapsible={true}
            expanded={tenantViewStore.getCardState("tenant")}
            toggleExpand={() => tenantViewStore.toggleCardState("tenant")}
          >
            <FormControl fullWidth>
              <ShowMultiLine value={name} label={langStore.TenantNameLabel} />
              <ShowReference
                label={langStore.TenantTenantTypeLabel}
                selectedValue={langStore.displayIdByLang(
                  tenantTypeValue?.displayId
                )}
              />
              <ShowReference
                label={langStore.TenantStatusLabel}
                selectedValue={langStore.displayIdByLang(
                  statusValue?.displayId
                )}
              />
              <ShowReference
                label={langStore.TenantPlanLabel}
                selectedValue={langStore.displayIdByLang(planValue?.displayId)}
              />
              <ShowFilesCompact
                rows={logoFiles}
                showLabel={true}
                label={langStore.TenantLogoLabel}
                menuActions={logoViewAction}
                columns={[
                  { name: "mimetype", title: langStore.fileTypeLabel },
                  { name: "name", title: langStore.fileNameLabel },
                  { name: "formattedSize", title: langStore.fileSizeLabel },
                ]}
              />
              <ShowMultiLine
                value={publicUrl}
                label={langStore.TenantPublicUrlLabel}
              />
              <ShowMultiLine value={idERP} label={langStore.TenantIdERPLabel} />
              <ShowMultiLine value={idCRM} label={langStore.TenantIdCRMLabel} />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="llmLimits"
            title={langStore.groupTenantLlmLimitsLabel}
            collapsible={true}
            expanded={tenantViewStore.getCardState("llmLimits")}
            toggleExpand={() => tenantViewStore.toggleCardState("llmLimits")}
          >
            <FormControl fullWidth>
              <ShowNumber
                value={monthlyTokenLimit}
                label={langStore.TenantMonthlyTokenLimitLabel}
              />
              <ShowNumber
                value={currentMonthTokens}
                label={langStore.TenantCurrentMonthTokensLabel}
              />
              <ShowNumber
                value={tokenLimitResetDay}
                label={langStore.TenantTokenLimitResetDayLabel}
              />
              <ShowDateTime
                value={lastTokenResetDate}
                label={langStore.TenantLastTokenResetDateLabel}
              />
              <ShowReference
                label={langStore.TenantRagLlmProviderLabel}
                selectedValue={langStore.displayIdByLang(
                  ragLlmProviderValue?.displayId
                )}
              />
              <ShowMultiLine
                value={ragLlmModel}
                label={langStore.TenantRagLlmModelLabel}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="storageLimits"
            title={langStore.groupTenantStorageLimitsLabel}
            collapsible={true}
            expanded={tenantViewStore.getCardState("storageLimits")}
            toggleExpand={() =>
              tenantViewStore.toggleCardState("storageLimits")
            }
          >
            <FormControl fullWidth>
              <ShowNumber
                value={currentStorageBytes}
                label={langStore.TenantCurrentStorageBytesLabel}
              />
              <ShowNumber
                value={storageByteLimit}
                label={langStore.TenantStorageByteLimitLabel}
              />
            </FormControl>
          </GroupContainer>
        </Box>
      </Grid>
    );
  }
);
